import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Button,
    IconButton,
    Box,
    Snackbar,
    Alert,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import axios from "axios";
import ApiConfig from "../../Config";

const EditAdminDialog = ({ open, onClose, staffData, onSave }) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    const accentColor = isDark ? "#D1B99A" : "#806B52";
    const accentHoverColor = isDark ? "#BFA786" : "#6D5B45";

    const [formData, setFormData] = useState({
        name: "",
        contact: "",
        dob: "",
        role: "",
        address: {
            district: "",
            city: "",
            state: "",
            country: "",
            pinCode: "",
        },
    });

    const [loading, setLoading] = useState(false);
    const [roleError, setRoleError] = useState("");
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");

    const roles = [
        {
            value: "ADMIN",
            label: "Admin",
        },
        {
            value: "STANDARD",
            label: "Standard",
        },
        {
            value: "MARKETING",
            label: "Marketing",
        },
    ];

    const normalizeRole = (role) => {
        const value = String(role || "")
            .trim()
            .toUpperCase()
            .replace(/[\s-]+/g, "_");

        if (
            value === "SUPERADMIN" ||
            value === "SUPER_ADMIN"
        ) {
            return "SUPER_ADMIN";
        }

        if (value === "ADMIN") {
            return "ADMIN";
        }

        if (value === "STANDARD") {
            return "STANDARD";
        }

        if (value === "MARKETING") {
            return "MARKETING";
        }

        return value;
    };

    useEffect(() => {
        if (!staffData) return;

        let formattedDob = "";

        const dobValue =
            staffData.dob ||
            staffData.admin_DOB ||
            "";

        if (dobValue) {
            const dobString = String(dobValue);

            const dobParts = dobString.split("/");

            if (dobParts.length === 3) {
                const [day, month, year] = dobParts;

                formattedDob = `${year}-${month.padStart(
                    2,
                    "0"
                )}-${day.padStart(2, "0")}`;
            } else if (dobString.includes("-")) {
                formattedDob = dobString;
            }
        }

        setFormData({
            name: staffData.name || "",
            contact: staffData.contact || "",
            dob: formattedDob,
            role: normalizeRole(staffData.role),
            address: {
                district:
                    staffData.address?.district || "",
                city:
                    staffData.address?.city || "",
                state:
                    staffData.address?.state || "",
                country:
                    staffData.address?.country || "",
                pinCode:
                    staffData.address?.pinCode || "",
            },
        });

        setRoleError("");
    }, [staffData]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        if (name === "role") {
            setFormData((prevData) => ({
                ...prevData,
                role: normalizeRole(value),
            }));

            setRoleError("");
            return;
        }

        if (name.startsWith("address.")) {
            const addressField = name.split(".")[1];

            setFormData((prevData) => ({
                ...prevData,
                address: {
                    ...prevData.address,
                    [addressField]: value,
                },
            }));

            return;
        }

        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    const handleSaveChanges = async () => {
        const targetAdminId =
            staffData?.employeeId ||
            staffData?.adminId;

        if (!targetAdminId) {
            setSnackbarMessage("Admin ID is missing");
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
            return;
        }

        if (!formData.role) {
            setRoleError("Role is required");
            setSnackbarMessage("Please select a role");
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
            return;
        }

        if (formData.role === "SUPER_ADMIN") {
            setRoleError("Super Admin cannot be assigned");
            setSnackbarMessage(
                "Super Admin role cannot be assigned."
            );
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
            return;
        }

        if (!formData.name.trim()) {
            setSnackbarMessage("Name is required");
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
            return;
        }

        setLoading(true);
        setSnackbarMessage("");

        try {
            const token = sessionStorage.getItem("newToken");

            if (!token) {
                setSnackbarMessage(
                    "Authentication token is missing"
                );
                setSnackbarSeverity("error");
                setSnackbarOpen(true);
                return;
            }

            let formattedDOB = "";

            if (formData.dob) {
                const [year, month, day] =
                    formData.dob.split("-");

                if (year && month && day) {
                    formattedDOB = `${day}/${month}/${year}`;
                }
            }

            const payload = {
                name: formData.name.trim(),
                contact: formData.contact.trim(),
                admin_DOB: formattedDOB,
                role: formData.role,
                address: {
                    district:
                        formData.address.district.trim(),
                    city:
                        formData.address.city.trim(),
                    state:
                        formData.address.state.trim(),
                    country:
                        formData.address.country.trim(),
                    pinCode:
                        formData.address.pinCode.trim(),
                },
            };

            const response = await axios.post(
                `${ApiConfig.apiBaseUrl}${ApiConfig.editAdmin}`,
                payload,
                {
                    headers: {
                        "x-access-token": token,
                        "admin-id": targetAdminId,
                    },
                }
            );

            if (
                response.status === 200 ||
                response.status === 201
            ) {
                setSnackbarMessage(
                    response.data?.message ||
                        "Staff member updated successfully"
                );
                setSnackbarSeverity("success");
                setSnackbarOpen(true);

                if (onSave) {
                    await onSave();
                }

                setTimeout(() => {
                    onClose();
                }, 300);
            } else {
                setSnackbarMessage(
                    "Unexpected response. Please try again."
                );
                setSnackbarSeverity("info");
                setSnackbarOpen(true);
            }
        } catch (err) {
            const errorMessage =
                err.response?.data?.message ||
                err.response?.data?.error ||
                "An error occurred. Please try again.";

            setSnackbarMessage(errorMessage);
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
        } finally {
            setLoading(false);
        }
    };

    const handleCloseDialog = () => {
        if (loading) return;

        setFormData({
            name: "",
            contact: "",
            dob: "",
            role: "",
            address: {
                district: "",
                city: "",
                state: "",
                country: "",
                pinCode: "",
            },
        });

        setRoleError("");
        setSnackbarOpen(false);

        onClose();
    };

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    const inputSx = {
        mb: 2,

        "& .MuiOutlinedInput-root": {
            borderRadius: 2,
            backgroundColor: "background.paper",
            transition: "all 0.2s ease",

            "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: accentColor,
            },

            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: accentColor,
                borderWidth: "1px",
            },
        },

        "& .MuiInputLabel-root.Mui-focused": {
            color: accentColor,
        },
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={handleCloseDialog}
                fullWidth
                maxWidth="sm"
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        backgroundColor: "background.paper",
                        color: "text.primary",
                        border: "1px solid",
                        borderColor: "divider",
                        boxShadow: isDark
                            ? "0 20px 60px rgba(0,0,0,0.45)"
                            : "0 20px 60px rgba(65,45,25,0.14)",
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        px: 3,
                        py: 2.5,
                        borderBottom: "1px solid",
                        borderColor: "divider",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                        }}
                    >
                        <Box>
                            <Typography
                                variant="h6"
                                fontWeight={700}
                                sx={{
                                    color: "text.primary",
                                    letterSpacing: "-0.2px",
                                }}
                            >
                                Edit Staff Member
                            </Typography>

                            <Typography
                                variant="body2"
                                sx={{
                                    mt: 0.5,
                                    color: "text.secondary",
                                }}
                            >
                                Update staff profile, role and contact
                                information
                            </Typography>
                        </Box>

                        <IconButton
                            aria-label="close"
                            onClick={handleCloseDialog}
                            disabled={loading}
                            sx={{
                                color: "text.secondary",
                                borderRadius: 2,

                                "&:hover": {
                                    color: accentColor,
                                    backgroundColor: isDark
                                        ? "rgba(209,185,154,0.08)"
                                        : "rgba(128,107,82,0.08)",
                                },
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent
                    dividers
                    sx={{
                        px: 3,
                        py: 3,
                        borderColor: "divider",
                    }}
                >
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "1fr",
                                md: "1fr 1fr",
                            },
                            gap: 3,
                        }}
                    >
                        <Box>
                            <TextField
                                autoFocus
                                label="Name"
                                name="name"
                                type="text"
                                fullWidth
                                variant="outlined"
                                value={formData.name}
                                onChange={handleInputChange}
                                disabled={loading}
                                sx={inputSx}
                            />

                            <TextField
                                label="Contact"
                                name="contact"
                                type="text"
                                fullWidth
                                variant="outlined"
                                value={formData.contact}
                                onChange={handleInputChange}
                                disabled={loading}
                                sx={inputSx}
                            />

                            <TextField
                                label="Date of Birth"
                                name="dob"
                                type="date"
                                fullWidth
                                variant="outlined"
                                value={formData.dob}
                                onChange={handleInputChange}
                                disabled={loading}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                sx={inputSx}
                            />

                            <FormControl
                                fullWidth
                                error={!!roleError}
                                disabled={loading}
                                sx={inputSx}
                            >
                                <InputLabel id="edit-role-label">
                                    Role
                                </InputLabel>

                                <Select
                                    labelId="edit-role-label"
                                    name="role"
                                    value={
                                        formData.role === "SUPER_ADMIN"
                                            ? ""
                                            : formData.role
                                    }
                                    onChange={handleInputChange}
                                    label="Role"
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                mt: 1,
                                                borderRadius: 2,
                                                backgroundColor:
                                                    "background.paper",
                                                border: "1px solid",
                                                borderColor:
                                                    "divider",
                                            },
                                        },
                                    }}
                                >
                                    {roles.map((role) => (
                                        <MenuItem
                                            key={role.value}
                                            value={role.value}
                                        >
                                            {role.label}
                                        </MenuItem>
                                    ))}
                                </Select>

                                {roleError && (
                                    <FormHelperText>
                                        {roleError}
                                    </FormHelperText>
                                )}
                            </FormControl>
                        </Box>

                        <Box>
                            <TextField
                                label="District"
                                name="address.district"
                                value={
                                    formData.address.district
                                }
                                onChange={handleInputChange}
                                fullWidth
                                variant="outlined"
                                disabled={loading}
                                sx={inputSx}
                            />

                            <TextField
                                label="City"
                                name="address.city"
                                value={formData.address.city}
                                onChange={handleInputChange}
                                fullWidth
                                variant="outlined"
                                disabled={loading}
                                sx={inputSx}
                            />

                            <TextField
                                label="State"
                                name="address.state"
                                value={formData.address.state}
                                onChange={handleInputChange}
                                fullWidth
                                variant="outlined"
                                disabled={loading}
                                sx={inputSx}
                            />

                            <TextField
                                label="Country"
                                name="address.country"
                                value={
                                    formData.address.country
                                }
                                onChange={handleInputChange}
                                fullWidth
                                variant="outlined"
                                disabled={loading}
                                sx={inputSx}
                            />

                            <TextField
                                label="Pin Code"
                                name="address.pinCode"
                                value={
                                    formData.address.pinCode
                                }
                                onChange={handleInputChange}
                                fullWidth
                                variant="outlined"
                                disabled={loading}
                                sx={inputSx}
                            />
                        </Box>
                    </Box>
                </DialogContent>

                <DialogActions
                    sx={{
                        px: 3,
                        py: 2.5,
                        borderTop: "1px solid",
                        borderColor: "divider",
                        gap: 1,
                    }}
                >
                    <Button
                        variant="outlined"
                        onClick={handleCloseDialog}
                        disabled={loading}
                        sx={{
                            minWidth: 100,
                            borderRadius: 2,
                            textTransform: "none",
                            fontWeight: 600,
                            color: "text.primary",
                            borderColor: "divider",

                            "&:hover": {
                                borderColor: accentColor,
                                backgroundColor: isDark
                                    ? "rgba(209,185,154,0.06)"
                                    : "rgba(128,107,82,0.06)",
                            },
                        }}
                    >
                        Cancel
                    </Button>

                    <Button
                        variant="contained"
                        onClick={handleSaveChanges}
                        disabled={loading}
                        sx={{
                            minWidth: 130,
                            borderRadius: 2,
                            textTransform: "none",
                            fontWeight: 700,
                            backgroundColor: accentColor,
                            color: isDark
                                ? "#211F1C"
                                : "#FFFFFF",
                            boxShadow: "none",

                            "&:hover": {
                                backgroundColor:
                                    accentHoverColor,
                                boxShadow: "none",
                            },
                        }}
                    >
                        {loading
                            ? "Updating..."
                            : "Update Staff"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    severity={snackbarSeverity}
                    variant="filled"
                    sx={{
                        width: "100%",
                        borderRadius: 2,
                    }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </>
    );
};

export default EditAdminDialog;